<template>
  <LoaderWrapper ref="loader" class="h-100">
    <div class="row m-4 mt-0 mb-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0">
          <div class="row">
            <div class="form-group col-sm-12">
              <ul class="navbar-nav overflow-auto flex-row gap-4">
                <li class="nav-item nav-item-spaced d-lg-block text-nowrap">
                  <a
                    href="#"
                    @click.prevent="selectSubTab('general')"
                    class="nav-link tab"
                    :class="{ active: activeSubTab === 'general' }"
                  >{{ helper.trans('statistic.conversion.tab_general') }}</a>
                </li>
                <li class="nav-item nav-item-spaced d-lg-block text-nowrap">
                  <a
                    href="#"
                    @click.prevent="selectSubTab('counterparty')"
                    class="nav-link tab"
                    :class="{ active: activeSubTab === 'counterparty' }"
                  >{{ helper.trans('statistic.conversion.tab_counterparty') }}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row m-4 mt-0 mb-0">
      <div class="col-sm-12">
        <div class="row">
          <div class="col-sm-12">
            <div class="card card-body mb-0">
              <div class="row">
                <div class="form-group mb-2 col-md-3 col-lg-3 col-xl-2 me-2">
                  <div class="col p-0">
                    <StoreMultiselect :multiple="true" v-model="storesFilter" />
                  </div>
                </div>

                <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
                  <div class="col p-0">
                    <DatePicker v-model="requestDTO.fromDate" mode="dateTime" :model-modifiers="{string: true}" is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
                      <template v-slot="{ inputValue, togglePopover, inputEvents }">
                        <input
                          class="form-control form-control-sm"
                          :placeholder="helper.trans('labels.from_date')"
                          :value="inputValue"
                          @focus="togglePopover"
                          @change="inputEvents.change"
                        />
                      </template>
                    </DatePicker>
                  </div>
                </div>

                <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
                  <div class="col p-0">
                    <DatePicker v-model="requestDTO.toDate" mode="dateTime" :model-modifiers="{string: true}" is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
                      <template v-slot="{ inputValue, togglePopover, inputEvents }">
                        <input
                          class="form-control form-control-sm"
                          :placeholder="helper.trans('labels.to_date')"
                          :value="inputValue"
                          @focus="togglePopover"
                          @change="inputEvents.change"
                        />
                      </template>
                    </DatePicker>
                  </div>
                </div>

                <div v-if="activeSubTab === 'general'" class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
                  <div class="col p-0">
                    <input type="number" v-model.number="requestDTO.minUseCount" class="form-control form-control-sm" :placeholder="helper.trans('statistic.conversion.min_use_count')" min="0" />
                  </div>
                </div>

                <div v-if="activeSubTab === 'counterparty'" class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
                  <div class="col p-0">
                    <input type="text" v-model.trim="kaUserId" class="form-control form-control-sm" :placeholder="helper.trans('statistic.conversion.search_user_id')" autocomplete="off" />
                  </div>
                </div>

                <div class="form-group mb-2 col">
                  <div class="col-auto p-0 text-end">
                    <button type="button" @click.prevent="applyFilters()" class="btn btn-outline-primary btn-sm rounded-pill me-2 text-capitalize-first">{{ helper.trans('button.apply') }}</button>
                    <button type="button" @click.prevent="clearFilters()" class="btn btn-outline-secondary btn-sm rounded-pill text-capitalize-first">{{ helper.trans('button.reset') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="activeSubTab === 'general'" class="row m-4 mb-0 mt-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0">
          <div class="overflow-auto">
            <table class="table table-bordered align-middle text-left text-nowrap position-relative">
              <thead>
                <tr class="bg-light">
                  <th class="text-capitalize-first">{{ helper.trans('labels.user_id') }}</th>
                  <th class="text-capitalize-first">{{ helper.trans('labels.store_id') }}</th>
                  <th class="text-capitalize-first">{{ helper.trans('statistic.conversion.total_use_count') }}</th>
                  <th class="text-capitalize-first">{{ helper.trans('statistic.conversion.total_completed') }}</th>
                  <th class="text-capitalize-first">{{ helper.trans('statistic.conversion.total_conversion_rate') }}</th>
                  <th class="text-capitalize-first">{{ helper.trans('labels.first_used_at') }}</th>
                  <th class="text-capitalize-first">{{ helper.trans('labels.last_used_at') }}</th>
                  <th class="text-capitalize-first">{{ helper.trans('labels.is_banned') }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in response.items" :key="index">
                  <td>
                    <span v-b-tooltip.hover :title="item.userId">{{ item.userId.substring(0, 8) }}...</span>
                    <copy-button class="ms-1" :value="item.userId"></copy-button>
                  </td>
                  <td>{{ item.storeId }}</td>
                  <td class="text-end">{{ numberFormatter.formatNumber(item.totalUseCount) }}</td>
                  <td class="text-end">{{ numberFormatter.formatNumber(item.totalCompleted) }}</td>
                  <td class="text-end">{{ numberFormatter.formatNumber(item.totalConversionRate, 2) }}%</td>
                  <td>
                    <span v-if="item.firstUsedAt">{{ dateHelper.format(item.firstUsedAt, 'YYYY-MM-DD') }}</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <span v-if="item.lastUsedAt">{{ dateHelper.format(item.lastUsedAt, 'YYYY-MM-DD') }}</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <span v-if="item.isBanned" class="badge bg-danger">{{ helper.trans('labels.banned') }}</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="text-end">
                    <button v-if="!item.isBanned" type="button" @click="openBanPopup(item)" class="btn btn-sm btn-outline-danger text-nowrap text-capitalize-first">
                      {{ helper.trans('button.ban') }}
                    </button>
                  </td>
                </tr>
                <tr v-if="response.items.length === 0">
                  <td colspan="9" class="text-center text-muted py-3">
                    {{ helper.trans('labels.no_history') }}
                  </td>
                </tr>
              </tbody>
            </table>

            <b-pagination
              v-if="response.totalCount > response.items.length"
              v-model="page"
              :total-rows="response.totalCount"
              :per-page="response.limit"
              :limit="20"
              @update:modelValue="load()"
              prev-text="Back"
              next-text="Next"
              align="start"
              first-number
              hide-goto-end-buttons
              hide-ellipsis
            ></b-pagination>
          </div>
        </div>
      </div>
    </div>

    <div v-if="activeSubTab === 'counterparty'" class="row m-4 mb-0 mt-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0">
          <div class="overflow-auto">
            <table class="table table-bordered align-middle text-left text-nowrap position-relative">
              <thead>
                <tr class="bg-light">
                  <th class="text-capitalize-first">{{ helper.trans('labels.store_id') }}</th>
                  <th class="text-capitalize-first">{{ helper.trans('statistic.conversion.counterparty_id') }}</th>
                  <th class="text-capitalize-first">{{ helper.trans('statistic.conversion.trader_id') }}</th>
                  <th class="text-capitalize-first">{{ helper.trans('statistic.conversion.issued_deals') }}</th>
                  <th class="text-capitalize-first">{{ helper.trans('statistic.conversion.not_issued') }}</th>
                  <th class="text-capitalize-first">{{ helper.trans('statistic.conversion.total_conversion_rate') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in kaResponse.items" :key="`${item.storeId}-${item.userId}-${item.traderId}`">
                  <td>{{ item.storeId }}</td>
                  <td>
                    <span v-b-tooltip.hover :title="item.userId">{{ item.userId.substring(0, 8) }}...</span>
                    <copy-button class="ms-1" :value="item.userId"></copy-button>
                  </td>
                  <td class="text-end">{{ item.traderId }}</td>
                  <td class="text-end">{{ numberFormatter.formatNumber(item.issuedCount) }}</td>
                  <td class="text-end">{{ numberFormatter.formatNumber(item.notIssuedCount) }}</td>
                  <td class="text-end">{{ numberFormatter.formatNumber(item.conversionRate, 2) }}%</td>
                </tr>
                <tr v-if="kaResponse.items.length === 0">
                  <td colspan="6" class="text-center text-muted py-3">
                    {{ helper.trans('labels.no_history') }}
                  </td>
                </tr>
              </tbody>
            </table>

            <b-pagination
              v-if="kaResponse.totalCount > kaResponse.items.length"
              v-model="kaPage"
              :total-rows="kaResponse.totalCount"
              :per-page="kaResponse.limit"
              :limit="20"
              @update:modelValue="loadKa()"
              prev-text="Back"
              next-text="Next"
              align="start"
              first-number
              hide-goto-end-buttons
              hide-ellipsis
            ></b-pagination>
          </div>
        </div>
      </div>
    </div>
    <MerchantUserBanModal ref="banPopup" @banned="onBanned" />
  </LoaderWrapper>
</template>

<script lang="ts">
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import { defineComponent, onMounted, reactive, ref, toRefs } from "vue";
import { dateHelper } from "@/service/DateHelper";
import { numberFormatter } from "@/service/NumberFormatter";
import { api } from "@/service/APIClient";
import { helper } from "@/service/CommonHelper";
import { useNotification } from "@kyvg/vue3-notification";
import { DatePicker } from "v-calendar";
import StoreMultiselect from "@/components/StoreMultiselect.vue";
import MerchantStoreDTO from "@/models/MerchantStoreDTO";
import PaginatedResponseDTO from "@/models/PaginatedResponseDTO";
import MerchantUserStatisticsDTO from "@/models/MerchantUserStatisticsDTO";
import CounterpartyConversionStatisticsDTO from "@/models/CounterpartyConversionStatisticsDTO";
import { BPagination } from "bootstrap-vue-3";
import MerchantUserBanModal from "@/components/MerchantUserBanModal.vue";
import CopyButton from "@/components/CopyButton.vue";
import moment from "moment";

const notification = useNotification();

function defaultRequestDTO() {
  return {
    fromDate: moment().subtract(30, "days").format("YYYY-MM-DD 00:00:00"),
    toDate: null as string | null,
    minUseCount: null as number | null,
  };
}

export default defineComponent({
  components: {
    LoaderWrapper,
    DatePicker,
    StoreMultiselect,
    BPagination,
    MerchantUserBanModal,
    CopyButton,
  },
  setup() {
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null);
    const banPopup = ref<InstanceType<typeof MerchantUserBanModal> | null>(null);

    const state = reactive({
      activeSubTab: "general" as "general" | "counterparty",
      storesFilter: [] as MerchantStoreDTO[],
      page: 1,
      response: {
        limit: 20,
        offset: 0,
        totalCount: 0,
        items: [],
      } as PaginatedResponseDTO<MerchantUserStatisticsDTO>,
      requestDTO: defaultRequestDTO(),
      kaPage: 1,
      kaUserId: "",
      kaResponse: {
        limit: 20,
        offset: 0,
        totalCount: 0,
        items: [],
      } as PaginatedResponseDTO<CounterpartyConversionStatisticsDTO>,
    });

    const buildStoreDateParams = () => {
      const storeIds = state.storesFilter.map((s) => s.id);
      const params: Record<string, unknown> = {};
      if (storeIds.length) params.storeIds = storeIds;
      if (state.requestDTO.fromDate) params.fromDate = dateHelper.toUTC(state.requestDTO.fromDate);
      if (state.requestDTO.toDate) params.toDate = dateHelper.toUTC(state.requestDTO.toDate);
      return params;
    };

    const notifyError = (error: unknown) => {
      notification.notify({
        type: "error",
        title: helper.trans("labels.error"),
        text: error instanceof Error ? error.message : String(error),
      });
    };

    const load = async () => {
      loader.value?.show();
      try {
        state.response.offset = state.response.limit * (state.page - 1);
        const params = buildStoreDateParams();
        if (state.requestDTO.minUseCount != null) params.minUseCount = state.requestDTO.minUseCount;
        state.response = await api.call.statistic.merchantUsers(state.response, params);
      } catch (error) {
        notifyError(error);
      } finally {
        loader.value?.hide();
      }
    };

    const loadKa = async () => {
      loader.value?.show();
      try {
        state.kaResponse.offset = state.kaResponse.limit * (state.kaPage - 1);
        const params = buildStoreDateParams();
        if (state.kaUserId) params.userId = state.kaUserId;
        state.kaResponse = await api.call.statistic.counterpartyConversion(state.kaResponse, params);
      } catch (error) {
        notifyError(error);
      } finally {
        loader.value?.hide();
      }
    };

    const resetPageAndLoad = (tab: "general" | "counterparty") => {
      if (tab === "general") {
        state.page = 1;
        load();
      } else {
        state.kaPage = 1;
        loadKa();
      }
    };

    const selectSubTab = (sub: "general" | "counterparty") => {
      if (state.activeSubTab === sub) return;
      state.activeSubTab = sub;
      resetPageAndLoad(sub);
    };

    const applyFilters = () => {
      resetPageAndLoad(state.activeSubTab);
    };

    const clearFilters = () => {
      state.storesFilter = [];
      state.requestDTO = defaultRequestDTO();
      state.kaUserId = "";
      resetPageAndLoad(state.activeSubTab);
    };

    const openBanPopup = (item: MerchantUserStatisticsDTO) => {
      banPopup.value?.show(item.storeId, item.userId);
    };

    const onBanned = () => {
      load();
    };

    onMounted(() => {
      load();
    });

    return {
      ...toRefs(state),
      loader,
      banPopup,
      dateHelper,
      numberFormatter,
      helper,
      load,
      loadKa,
      selectSubTab,
      applyFilters,
      clearFilters,
      openBanPopup,
      onBanned,
    };
  },
});
</script>
